// @flow

import { useRef, useState, useEffect, useCallback, useMemo } from 'react'

export const MILLISECONDS = 1000

export type UseTimerProps = {
  expireTime: number,
  step?: number,
  onUpdate?: (timeLeft: number) => void,
}

export const useTimer = ({ expireTime = 0, step = 1, onUpdate }: UseTimerProps) => {
  const ref = useRef(null)

  const [isRunning, setRunning] = useState(false)
  const preparedStep = useMemo(() => step * MILLISECONDS, [step])

  const handleStop = useCallback(() => {
    if (ref.current) {
      clearInterval(ref.current)
      setRunning(false)
    }
  }, [])

  const handleUpdate = useCallback(
    timeLeft => {
      // Convert milliseconds to seconds
      onUpdate && onUpdate(timeLeft / MILLISECONDS)
    },
    [onUpdate],
  )

  const handleStart = useCallback(() => {
    handleStop()
    setRunning(true)

    // Convert seconds to milliseconds
    let timeLeft = expireTime * MILLISECONDS

    // eslint-disable-next-line consistent-return,complexity
    ref.current = setInterval(() => {
      timeLeft -= preparedStep

      if (timeLeft <= 0) {
        handleStop()
        handleUpdate(timeLeft)
      } else {
        handleUpdate(timeLeft)
      }
    }, preparedStep)
  }, [handleStop, expireTime, preparedStep, handleUpdate])

  useEffect(
    () => () => {
      handleStop()
    },
    [], // eslint-disable-line react-hooks/exhaustive-deps
  )

  return {
    isRunning,
    start: handleStart,
    stop: handleStop,
  }
}

export default useTimer
